import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostBinding, Input, OnChanges, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BetslipConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { DateFormatterService } from '@frontend/sports/common/core/feature/date-formatter-service';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { DeviceService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { BetBuilderConfigService } from 'packages/sports/web/app/src/betbuilder/services/betbuilder-config.service';
import { EpcotConfigService } from 'packages/sports/web/app/src/common/epcot-config.service';

import { BetslipType } from '../../../core/betslip-type';
import { ExternalBetslipActions } from '../../../core/external-betslip-actions';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import BetslipPickUtilsService from '../../picks/services/betslip-pick-utils.service';
import { EntainUiBetBuilderPickRemovalTracking } from '../../tracking/tracking.models';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import { underMinimumBetBuilderSelections } from '../../validation/services/validators/betslip/min-selections-betbuilder.validator';
import { BaseBetBuilderComponent } from './base-betbuilder.component';
import { BetBuilderSelectionModel } from './betbuilder-selection.model';

@Component({
    selector: 'bs-betslip-betbuilder-pick',
    templateUrl: './betslip-betbuilder-pick.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BetslipBetBuilderPickComponent extends BaseBetBuilderComponent implements OnInit, OnChanges {
    @Input() pick: BetslipBetBuilderPick;
    date: string;

    @HostBinding('class') className = 'betBuilder-pick';
    get isEntainUi(): boolean {
        return BetslipBetBuilderPick.isEntainUiBetBuilderPick(this.pick);
    }

    override isEpcotEnabled: boolean;
    override isComboType: boolean;
    override isBetbuilderComboEnabled: boolean;
    override get selectionsLength(): number {
        return this.pick.getLegCount();
    }

    shouldShowMinSelectionError = false;

    constructor(
        private deviceService: DeviceService,
        sitecore: Sitecore,
        epcotConfigService: EpcotConfigService,
        private betBuilderConfigService: BetBuilderConfigService,
        private store: Store,
        interpolationPipe: StringInterpolationPipe,
        betslipConfig: BetslipConfig,
        change: ChangeDetectorRef,
        private betslipPickUtilsService: BetslipPickUtilsService,
        private dateFormatterService: DateFormatterService,
    ) {
        super(interpolationPipe, sitecore, epcotConfigService, change, betslipConfig);
    }

    ngOnInit(): void {
        this.store
            .select(betslipCurrentTypeSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((selectedType) => {
                const isComboType = selectedType === BetslipType.Combo;
                this.setIsCombo(isComboType);
            });
    }

    removeBetBuilderSelection(selection: BetBuilderSelectionModel): void {
        this.betslipPickUtilsService.trackEntainUiBetBuilderPickRemoval(EntainUiBetBuilderPickRemovalTracking.TrackingSinglePickRemoval);
        this.store.dispatch(
            //why calling external actions within betslip ?
            ExternalBetslipActions.removeEntainUiBetBuilderPick({
                pickId: this.pick.id,
                longId: selection.longId,
                eventId: this.pick.eventId,
            }),
        );
    }

    ngOnChanges(): void {
        this.shouldShowMinSelectionError = this.betBuilderConfigService.showEmbeddedUi() && underMinimumBetBuilderSelections(this.pick);
        this.date = this.dateFormatterService.formatDateWithTime(this.pick.eventDate);
    }

    get selections() {
        if (this.pick.sportcastOptions?.length) {
            return this.pick.sportcastOptions;
        }

        return this.pick.marketName.name.split(';').map((n) => ({ longId: '', name: n }));
    }

    get shouldShowRemoveButton(): boolean {
        return !this.deviceService.isMobile;
    }

    getClass(epcotClass: string, rowClass: string): string {
        return this.isEpcotEnabled ? (this.isCombo ? epcotClass : '') : rowClass;
    }
}
